import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TrendingUp, Plus, Edit2, X, CheckCircle, AlertCircle, Target, Search } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import WeightChart from '../../components/WeightChart';
import Modal from '../../components/Modal';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: 'waist', label: 'Waist', unit: 'in' },
  { key: 'hips', label: 'Hips', unit: 'in' },
  { key: 'thighs', label: 'Thighs', unit: 'in' },
  { key: 'chest', label: 'Chest', unit: 'in' },
  { key: 'biceps', label: 'Biceps', unit: 'in' },
  { key: 'forearms', label: 'Forearms', unit: 'in' },
];

const GOAL_TYPES = [
  { value: 'weight_loss', label: 'Weight Loss' },
  { value: 'weight_gain', label: 'Weight Gain' },
  { value: 'maintenance', label: 'Maintenance' },
];

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-lg text-sm font-body animate-fade-in-up
      ${type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
      {type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
      {message}
    </div>
  );
}

function today() { return new Date().toISOString().split('T')[0]; }

const EMPTY_FORM = { date: today(), weight: '', waist: '', hips: '', thighs: '', chest: '', biceps: '', forearms: '', notes: '' };

export default function AdminProgress() {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [goalForm, setGoalForm] = useState({ goal_weight: '', goal_type: '' });
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  // Searchable student selector state
  const [studentSearch, setStudentSearch] = useState('');
  const [studentDropdown, setStudentDropdown] = useState(false);
  const studentSelectorRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (studentSelectorRef.current && !studentSelectorRef.current.contains(e.target))
        setStudentDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/admin/students`, { headers: getAuthHeaders() })
      .then(r => setStudents(r.data.filter(s => !s.is_archived)))
      .catch(console.error);
  }, []);

  const fetchProgress = useCallback(async () => {
    if (!selectedStudent) return;
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/api/admin/progress?student_id=${selectedStudent}`, { headers: getAuthHeaders() });
      setData(d);
      setGoalForm({ goal_weight: d.goal_weight || '', goal_type: d.goal_type || '' });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedStudent]);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  const openAdd = () => { setEditEntry(null); setForm({ ...EMPTY_FORM, date: today() }); setShowForm(true); };
  const openEdit = (e) => {
    setEditEntry(e);
    setForm({ date: e.date, weight: e.weight || '', waist: e.waist || '', hips: e.hips || '', thighs: e.thighs || '', chest: e.chest || '', biceps: e.biceps || '', forearms: e.forearms || '', notes: e.notes || '' });
    setShowForm(true);
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!form.weight) { setToast({ message: 'Weight is required.', type: 'error' }); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])),
        weight: parseFloat(form.weight),
      };
      if (editEntry) {
        await axios.put(`${API}/api/admin/progress/${editEntry.id}`, payload, { headers: getAuthHeaders() });
        setToast({ message: 'Entry updated!', type: 'success' });
      } else {
        await axios.post(`${API}/api/admin/progress`, { ...payload, student_id: selectedStudent }, { headers: getAuthHeaders() });
        setToast({ message: 'Entry added!', type: 'success' });
      }
      setShowForm(false);
      fetchProgress();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Something went wrong.', type: 'error' });
    } finally { setSubmitting(false); }
  };

  const handleSaveGoal = async () => {
    setSubmitting(true);
    try {
      const payload = {};
      if (goalForm.goal_weight) payload.goal_weight = parseFloat(goalForm.goal_weight);
      if (goalForm.goal_type) payload.goal_type = goalForm.goal_type;
      await axios.put(`${API}/api/admin/students/${selectedStudent}/goal`, payload, { headers: getAuthHeaders() });
      setToast({ message: 'Goal updated!', type: 'success' });
      setShowGoalForm(false);
      fetchProgress();
    } catch { setToast({ message: 'Could not save goal.', type: 'error' }); }
    finally { setSubmitting(false); }
  };

  const entries = data?.entries || [];
  const latest = entries.length > 0 ? entries[entries.length - 1] : null;
  const goalWeight = data?.goal_weight;

  return (
    <div className="space-y-6 animate-fade-in-up">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-1">Track</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#292524]">Progress</h1>
      </div>

      {/* Searchable Student selector */}
      <div className="bg-white rounded-3xl p-5 shadow-card border border-[#E7E5E4]/50">
        <label className="block text-sm font-medium text-[#57534E] mb-2">Select Student</label>
        <div ref={studentSelectorRef} className="relative">
          {selectedStudent ? (
            /* Selected state — show chip */
            <div className="flex items-center justify-between gap-3 bg-[#FFFAF0] border-2 border-[#F59E0B]/40 rounded-2xl px-4 py-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-[#FFEDD5] rounded-xl flex items-center justify-center text-[#D97706] font-heading font-semibold text-xs shrink-0">
                  {students.find(s => s.id === selectedStudent)?.name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <p className="font-body text-[#292524] text-sm font-medium">{students.find(s => s.id === selectedStudent)?.name}</p>
                  <p className="font-body text-[#A8A29E] text-xs">{students.find(s => s.id === selectedStudent)?.phone}</p>
                </div>
              </div>
              <button type="button" onClick={() => { setSelectedStudent(''); setStudentSearch(''); setData(null); }}
                className="text-[#A8A29E] hover:text-red-500 transition-colors p-1" title="Change student">
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          ) : (
            /* Search state */
            <>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" strokeWidth={1.5} />
                <input
                  data-testid="progress-student-select"
                  type="text"
                  placeholder="Search student by name..."
                  value={studentSearch}
                  onChange={e => { setStudentSearch(e.target.value); setStudentDropdown(true); }}
                  onFocus={() => setStudentDropdown(true)}
                  autoComplete="off"
                  className="bg-[#FFFAF0] border-2 border-[#E7E5E4] rounded-2xl pl-11 pr-4 py-3 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body text-sm"
                />
              </div>
              {studentDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-[#E7E5E4] rounded-2xl shadow-xl z-20 overflow-hidden max-h-52 overflow-y-auto">
                  {students
                    .filter(s => !studentSearch || s.name.toLowerCase().includes(studentSearch.toLowerCase()) || s.phone.includes(studentSearch))
                    .map(s => (
                      <button key={s.id} type="button"
                        onMouseDown={() => { setSelectedStudent(s.id); setStudentSearch(''); setStudentDropdown(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FFFAF0] transition-colors text-left border-b border-[#E7E5E4]/40 last:border-0">
                        <div className="w-8 h-8 bg-[#FFEDD5] rounded-xl flex items-center justify-center text-[#D97706] font-heading font-semibold text-xs shrink-0">
                          {s.name?.[0]?.toUpperCase()}
                        </div>
                        <div>
                          <p className="font-body text-[#292524] text-sm font-medium">{s.name}</p>
                          <p className="font-body text-[#A8A29E] text-xs">{s.phone}</p>
                        </div>
                      </button>
                    ))
                  }
                  {studentSearch && !students.some(s => s.name.toLowerCase().includes(studentSearch.toLowerCase())) && (
                    <div className="px-4 py-3"><p className="text-sm text-[#A8A29E] font-body">No students found</p></div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {selectedStudent && loading && (
        <div className="flex justify-center py-10"><div className="w-10 h-10 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin" /></div>
      )}

      {selectedStudent && !loading && data && (
        <>
          {/* Goal + current weight */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-3xl p-5 shadow-card border border-[#E7E5E4]/50">
              <p className="text-xs text-[#A8A29E] font-body mb-1">Current Weight</p>
              <p className="font-heading text-3xl font-semibold text-[#292524]">
                {latest ? `${latest.weight} kg` : '—'}
              </p>
              {goalWeight && latest && (
                <p className={`text-sm font-body mt-1 ${latest.weight > goalWeight ? 'text-orange-500' : 'text-green-600'}`}>
                  {Math.abs(latest.weight - goalWeight).toFixed(1)} kg {latest.weight > goalWeight ? 'to lose' : 'ahead of goal'}
                </p>
              )}
            </div>
            <div className="bg-white rounded-3xl p-5 shadow-card border border-[#E7E5E4]/50">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-[#A8A29E] font-body">Goal Weight</p>
                <button onClick={() => setShowGoalForm(v => !v)} className="text-xs text-[#F59E0B] hover:underline font-body">Edit</button>
              </div>
              {showGoalForm ? (
                <div className="flex gap-2 mt-2">
                  <input type="number" placeholder="kg" value={goalForm.goal_weight}
                    onChange={e => setGoalForm(f => ({ ...f, goal_weight: e.target.value }))}
                    className="border-2 border-[#E7E5E4] rounded-xl px-3 py-2 text-sm outline-none w-24 focus:border-[#F59E0B] font-body" />
                  <select value={goalForm.goal_type} onChange={e => setGoalForm(f => ({ ...f, goal_type: e.target.value }))}
                    className="border-2 border-[#E7E5E4] rounded-xl px-3 py-2 text-sm outline-none flex-1 focus:border-[#F59E0B] font-body">
                    <option value="">Type...</option>
                    {GOAL_TYPES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                  <button onClick={handleSaveGoal} disabled={submitting} className="bg-[#F59E0B] text-white rounded-xl px-3 py-2 text-sm font-heading">Save</button>
                </div>
              ) : (
                <>
                  <p className="font-heading text-3xl font-semibold text-[#292524]">{goalWeight ? `${goalWeight} kg` : '—'}</p>
                  {data.goal_type && (
                    <span className="text-xs bg-[#FFEDD5] text-[#D97706] rounded-full px-2.5 py-1 font-body capitalize mt-1 inline-block">
                      {GOAL_TYPES.find(g => g.value === data.goal_type)?.label || data.goal_type}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Chart */}
          <div className="bg-white rounded-3xl p-5 shadow-card border border-[#E7E5E4]/50">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-medium text-[#292524]">Weight Trend</h2>
              <button data-testid="add-progress-entry" onClick={openAdd}
                className="flex items-center gap-1.5 bg-[#F59E0B] text-white rounded-full px-4 py-2 text-xs font-heading font-medium shadow-button hover:-translate-y-0.5 transition-all">
                <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Add Entry
              </button>
            </div>
            <WeightChart entries={entries} goalWeight={goalWeight} />
          </div>

          {/* History table */}
          {entries.length > 0 && (
            <div className="bg-white rounded-3xl shadow-card border border-[#E7E5E4]/50 overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E7E5E4]/60">
                <h2 className="font-heading font-medium text-[#292524]">Measurement History</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#FFFAF0]">
                    <tr>
                      {['Date', 'Weight', 'Waist', 'Hips', 'Thighs', 'Chest', 'Biceps', 'Forearms', ''].map(h => (
                        <th key={h} className="text-left text-xs font-semibold text-[#78716C] uppercase tracking-wider px-4 py-3 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E7E5E4]/40">
                    {[...entries].reverse().map(e => (
                      <tr key={e.id} data-testid={`progress-row-${e.id}`} className="hover:bg-[#FFFAF0]/60">
                        <td className="px-4 py-3 font-body text-[#292524] font-medium whitespace-nowrap">
                          {new Date(e.date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-4 py-3 font-body font-semibold text-[#F59E0B]">{e.weight} kg</td>
                        {['waist','hips','thighs','chest','biceps','forearms'].map(f => (
                          <td key={f} className="px-4 py-3 font-body text-[#57534E]">{e[f] ? `${e[f]}"` : '—'}</td>
                        ))}
                        <td className="px-4 py-3">
                          <button data-testid={`edit-entry-${e.id}`} onClick={() => openEdit(e)}
                            className="text-[#A8A29E] hover:text-[#F59E0B] transition-colors p-1">
                            <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {entries.length === 0 && (
            <div className="bg-white rounded-3xl p-10 text-center shadow-card border border-[#E7E5E4]/50">
              <TrendingUp className="w-10 h-10 text-[#F59E0B] mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-heading font-medium text-[#292524]">No entries yet</p>
              <p className="text-[#78716C] text-sm font-body mt-1">Add the first measurement to start tracking progress.</p>
              <button onClick={openAdd} className="mt-4 bg-[#F59E0B] text-white rounded-full px-6 py-2.5 text-sm font-heading font-medium shadow-button hover:-translate-y-0.5 transition-all">
                Add First Entry
              </button>
            </div>
          )}
        </>
      )}

      {!selectedStudent && (
        <div className="bg-white rounded-3xl p-10 text-center shadow-card border border-[#E7E5E4]/50">
          <Target className="w-10 h-10 text-[#F59E0B] mx-auto mb-3" strokeWidth={1.5} />
          <p className="font-heading font-medium text-[#292524]">Select a student above</p>
          <p className="text-[#78716C] text-sm font-body mt-1">Choose a student to view and manage their progress.</p>
        </div>
      )}

      {showForm && (
        <Modal
          title={editEntry ? 'Edit Entry' : 'New Measurement'}
          onClose={() => setShowForm(false)}
          footer={
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowForm(false)}
                className="flex-1 border-2 border-[#E7E5E4] text-[#57534E] rounded-full py-3 font-heading font-medium text-sm hover:border-[#F59E0B] transition-colors">Cancel</button>
              <button data-testid="submit-entry" type="submit" form="progress-form" disabled={submitting}
                className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-3 font-heading font-medium text-sm shadow-button disabled:opacity-60 transition-all">
                {submitting ? 'Saving...' : editEntry ? 'Update' : 'Add Entry'}
              </button>
            </div>
          }
        >
          <form id="progress-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[#57534E] mb-1.5">Date</label>
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-3 py-2.5 focus:border-[#F59E0B] outline-none w-full text-sm font-body" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#57534E] mb-1.5">Weight (kg) *</label>
                <input data-testid="entry-weight-input" type="number" step="0.1" placeholder="65.5" value={form.weight}
                  onChange={e => setForm(f => ({ ...f, weight: e.target.value }))}
                  className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-3 py-2.5 focus:border-[#F59E0B] outline-none w-full text-sm font-body" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {FIELDS.map(field => (
                <div key={field.key}>
                  <label className="block text-xs font-medium text-[#57534E] mb-1.5">{field.label} ({field.unit})</label>
                  <input type="number" step="0.1" placeholder="—" value={form[field.key]}
                    onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))}
                    className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-3 py-2.5 focus:border-[#F59E0B] outline-none w-full text-sm font-body" />
                </div>
              ))}
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
